<template>
  <section class="min-h-[calc(100vh-7rem)] bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <div class="rounded-[1.75rem] border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6 sm:py-6">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
          <button
            type="button"
            class="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
            aria-label="Back"
            @click="router.back()"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" aria-hidden="true">
              <path d="M15 18 9 12l6-6" />
            </svg>
          </button>
          <div class="min-w-0">
            <p class="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Account settings</p>
            <h1 class="mt-2 text-3xl font-bold text-slate-950">Update profile</h1>
            <p class="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Keep your public account details current. The fields here follow the profile information already used across your account pages.
            </p>
          </div>
        </div>
      </div>

      <div class="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside class="grid content-start gap-5 self-start xl:sticky xl:top-24">
          <section class="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div class="flex flex-col items-center text-center">
              <div class="relative">
                <img
                  class="h-32 w-32 rounded-full border-4 border-slate-100 object-cover shadow-sm"
                  :src="avatarPreview"
                  :alt="form.fullName || form.username || 'Profile photo'"
                />
                <button
                  type="button"
                  class="absolute bottom-2 right-2 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white bg-blue-600 text-white shadow-lg transition hover:bg-blue-700"
                  @click="triggerAvatarSelect"
                >
                  <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
                <input ref="avatarInput" class="hidden" type="file" accept="image/*" @change="handleAvatarChange" />
              </div>
            </div>
          </section>
        </aside>

        <form class="grid gap-6" @submit.prevent="submitProfile">
          <section class="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div class="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-4">
              <div>
                <p class="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Profile info</p>
                <h2 class="mt-2 text-2xl font-bold text-slate-950">Public account details</h2>
              </div>
            </div>

            <div class="mt-5 grid gap-5 sm:grid-cols-2">
              <label class="grid gap-2 text-sm font-bold text-slate-700">
                <span>Username</span>
                <input
                  v-model="form.username"
                  class="min-h-12 cursor-not-allowed rounded-2xl border border-slate-200 bg-slate-100 px-4 text-slate-500 outline-none"
                  type="text"
                  placeholder="@username"
                  readonly
                />
              </label>

              <label class="grid gap-2 text-sm font-bold text-slate-700">
                <span>Full name</span>
                <input
                  v-model="form.fullName"
                  class="min-h-12 rounded-2xl border border-slate-300 bg-white px-4 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  type="text"
                  placeholder="Your full name"
                />
              </label>

              <label class="grid gap-2 text-sm font-bold text-slate-700">
                <span>Phone number</span>
                <input
                  ref="phoneInput"
                  v-model="form.phone"
                  class="profile-phone-input min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  type="tel"
                  placeholder="Phone number"
                />
              </label>

              <label class="grid gap-2 text-sm font-bold text-slate-700">
                <span>Email ID</span>
                <input
                  v-model="form.email"
                  class="min-h-12 rounded-2xl border border-slate-300 bg-white px-4 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  type="email"
                  placeholder="you@example.com"
                />
              </label>
            </div>

            <div class="mt-5 rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p class="text-sm font-bold text-slate-900">About</p>
                  <p class="mt-1 text-sm text-slate-500">Short public description shown across your account profile.</p>
                </div>
                <button
                  type="button"
                  class="inline-flex min-h-9 items-center justify-center rounded-full bg-white px-4 text-sm font-bold text-blue-700 ring-1 ring-slate-200 transition hover:bg-blue-50"
                  @click="openAboutModal"
                >
                  Update description
                </button>
              </div>
              <p class="mt-4 text-sm leading-7 text-slate-600">{{ aboutPreview }}</p>
            </div>
          </section>

          <div class="flex justify-end">
            <button
              type="submit"
              class="nf-primary-button min-w-[12rem]"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? 'Saving...' : 'Submit' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="aboutModalOpen"
      class="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label="Update description"
      @click.self="closeAboutModal"
    >
      <div class="w-full max-w-md rounded-[1.5rem] border border-slate-200 bg-white shadow-2xl">
        <div class="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <h3 class="text-2xl font-bold text-slate-900">Update description</h3>
          <button
            type="button"
            class="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close description modal"
            @click="closeAboutModal"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="px-5 py-5">
          <label class="grid gap-2 text-sm font-bold text-slate-700">
            <span>About me</span>
            <textarea
              v-model="aboutDraft"
              rows="5"
              class="min-h-[140px] rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              placeholder="Tell people a bit about yourself"
            />
          </label>
        </div>

        <div class="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            class="inline-flex min-h-10 items-center justify-center rounded-xl bg-slate-500 px-4 text-sm font-bold text-white transition hover:bg-slate-600"
            @click="closeAboutModal"
          >
            Close
          </button>
          <button
            type="button"
            class="nf-primary-button"
            :disabled="descriptionSaving"
            @click="submitDescription"
          >
            {{ descriptionSaving ? 'Saving...' : 'Submit' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="avatarCropOpen"
      class="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label="Crop profile image"
      @click.self="closeAvatarCrop"
    >
      <div class="w-full max-w-md rounded-[1.5rem] border border-slate-200 bg-white shadow-2xl">
        <div class="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <h3 class="text-2xl font-bold text-slate-900">Crop profile image</h3>
          <button
            type="button"
            class="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close image crop modal"
            :disabled="avatarUploading"
            @click="closeAvatarCrop"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="grid gap-4 px-5 py-5">
          <div class="mx-auto aspect-square w-full max-w-[18rem] overflow-hidden rounded-full border border-slate-200 bg-slate-100">
            <img
              v-if="avatarCropSrc"
              class="h-full w-full object-cover"
              :src="avatarCropSrc"
              alt="Selected profile preview"
              :style="{ transform: `scale(${avatarZoom})` }"
            />
          </div>
          <label class="grid gap-2 text-sm font-bold text-slate-700">
            <span>Zoom</span>
            <input
              v-model.number="avatarZoom"
              type="range"
              min="1"
              max="2"
              step="0.05"
              class="w-full accent-blue-600"
            />
          </label>
        </div>

        <div class="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            class="inline-flex min-h-10 items-center justify-center rounded-xl bg-slate-500 px-4 text-sm font-bold text-white transition hover:bg-slate-600"
            :disabled="avatarUploading"
            @click="closeAvatarCrop"
          >
            Cancel
          </button>
          <button
            type="button"
            class="nf-primary-button"
            :disabled="avatarUploading"
            @click="uploadCroppedAvatar"
          >
            {{ avatarUploading ? 'Uploading...' : 'Crop & upload' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'
import intlTelInput from 'intl-tel-input'
import 'intl-tel-input/build/css/intlTelInput.css'
import Swal from 'sweetalert2'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'

useHead({
  title: 'Edit Profile | NetFlyr',
  meta: [
    { name: 'description', content: 'Edit your NetFlyr profile details, avatar, contact information, business account fields, public description, and location data so your local flyer presence stays accurate.' },
    { name: 'keywords', content: 'edit NetFlyr profile, update profile, account profile, avatar, contact information, business profile, local flyer profile' }
  ]
})

const router = useRouter()
const session = useSessionStore()

const avatarInput = ref(null)
const avatarPreview = ref('/assets/img/av.png')
const phoneInput = ref(null)
const phoneCode = ref('+1')
const isSubmitting = ref(false)
const aboutModalOpen = ref(false)
const aboutDraft = ref('')
const descriptionSaving = ref(false)
const avatarCropOpen = ref(false)
const avatarCropSrc = ref('')
const avatarCropFile = ref(null)
const avatarZoom = ref(1)
const avatarUploading = ref(false)
let phoneInstance = null

const form = reactive({
  username: '',
  fullName: '',
  phone: '',
  phone_code: '+1',
  email: '',
  about: '',
  profile_image: ''
})

const phoneCodeToIso2 = (code = '') => {
  const normalized = String(code).trim()
  if (normalized === '+91' || normalized === '91') return 'in'
  if (normalized === '+44' || normalized === '44') return 'gb'
  if (normalized === '+1' || normalized === '1') return 'us'
  return 'us'
}

const aboutPreview = computed(() => {
  const value = String(form.about || '').trim()
  return value || 'No description added yet.'
})

const getCurrentUserId = () => String(session.userId || session.user?.user_id || session.user?.id || '').trim()

const getApiMessage = (payload, fallback) =>
  payload?.message || payload?.msg || payload?.errors?.[0]?.msg || fallback

const isSuccessResponse = (payload) => {
  const status = String(payload?.status ?? '').trim().toLowerCase()
  return !status || ['ok', 'success', 'true', '1'].includes(status)
}

const buildProfileSource = () => {
  const profile = session.profile || {}
  const nestedProfile = profile.userinfo || {}
  const user = session.user || {}

  return {
    ...user,
    ...profile,
    ...nestedProfile
  }
}

const hydrateForm = () => {
  const source = buildProfileSource()
  form.username = source.username || source.user_name || ''
  form.fullName = source.name || source.full_name || ''
  form.phone = source.phone || source.mobile || source.phone_number || ''
  form.phone_code = source.phone_code || source.country_code || source.dial_code || '+1'
  phoneCode.value = form.phone_code
  form.email = source.email || ''
  form.about = source.about || source.about_me || source.bio || source.description || ''
  form.profile_image = source.user_img || source.profile_pic || source.profile_image || source.image || ''
  avatarPreview.value = form.profile_image || '/assets/img/av.png'
}

const syncPhoneData = () => {
  const selectedCountry = phoneInstance?.getSelectedCountryData?.()
  phoneCode.value = selectedCountry?.dialCode ? `+${selectedCountry.dialCode}` : phoneCode.value || '+1'
  form.phone_code = phoneCode.value
  form.phone = phoneInput.value?.value?.trim() || form.phone
}

const setupPhoneInput = async () => {
  await nextTick()
  if (!phoneInput.value || phoneInstance) return

  phoneInstance = intlTelInput(phoneInput.value, {
    initialCountry: phoneCodeToIso2(phoneCode.value || form.phone_code),
    separateDialCode: true,
    nationalMode: true,
    autoPlaceholder: 'polite',
    utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@25.10.8/build/js/utils.js'
  })

  phoneInput.value.addEventListener('countrychange', syncPhoneData)
  phoneInput.value.addEventListener('input', syncPhoneData)
  syncPhoneData()
}

const destroyPhoneInput = () => {
  if (phoneInput.value) {
    phoneInput.value.removeEventListener('countrychange', syncPhoneData)
    phoneInput.value.removeEventListener('input', syncPhoneData)
  }

  if (phoneInstance) {
    phoneInstance.destroy()
    phoneInstance = null
  }
}

const triggerAvatarSelect = () => {
  avatarInput.value?.click()
}

const handleAvatarChange = (event) => {
  const file = event.target?.files?.[0]
  if (!file) return

  const reader = new FileReader()
  reader.onload = () => {
    const result = typeof reader.result === 'string' ? reader.result : ''
    if (!result) return
    avatarCropSrc.value = result
    avatarCropFile.value = file
    avatarZoom.value = 1
    avatarCropOpen.value = true
  }
  reader.readAsDataURL(file)
  event.target.value = ''
}

const closeAvatarCrop = (force = false) => {
  if (avatarUploading.value && !force) return
  avatarCropOpen.value = false
  avatarCropSrc.value = ''
  avatarCropFile.value = null
  avatarZoom.value = 1
}

const createCroppedAvatarFile = () =>
  new Promise((resolve, reject) => {
    if (!avatarCropSrc.value) {
      reject(new Error('Choose an image first.'))
      return
    }

    const image = new Image()
    image.onload = () => {
      const outputSize = 512
      const canvas = document.createElement('canvas')
      canvas.width = outputSize
      canvas.height = outputSize
      const context = canvas.getContext('2d')
      if (!context) {
        reject(new Error('Image could not be cropped.'))
        return
      }

      const sourceSize = Math.min(image.naturalWidth, image.naturalHeight) / Math.max(avatarZoom.value, 1)
      const sourceX = Math.max(0, (image.naturalWidth - sourceSize) / 2)
      const sourceY = Math.max(0, (image.naturalHeight - sourceSize) / 2)
      context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, outputSize, outputSize)
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Image could not be cropped.'))
          return
        }
        const originalName = avatarCropFile.value?.name || 'profile-image.jpg'
        resolve(new File([blob], originalName, { type: blob.type || 'image/jpeg' }))
      }, 'image/jpeg', 0.9)
    }
    image.onerror = () => reject(new Error('Image could not be loaded.'))
    image.src = avatarCropSrc.value
  })

const extractProfileImage = (payload) => {
  const data = payload?.data && typeof payload.data === 'object'
    ? Array.isArray(payload.data) ? payload.data[0] || {} : payload.data
    : {}
  return data.user_img || data.profile_pic || data.profile_image || data.image || payload?.user_img || payload?.profile_image || ''
}

const uploadCroppedAvatar = async () => {
  const id = getCurrentUserId()
  if (!id || avatarUploading.value) {
    if (!id) {
      await Swal.fire({
        icon: 'error',
        title: 'Login required',
        text: 'Sign in again before updating your profile image.',
        confirmButtonColor: '#2563eb'
      })
    }
    return
  }

  avatarUploading.value = true
  try {
    const croppedFile = await createCroppedAvatarFile()
    const { data } = await api.editProfileImage({
      type: 'image',
      id,
      user_img: croppedFile
    })

    if (!isSuccessResponse(data)) {
      throw new Error(getApiMessage(data, 'Profile image could not be uploaded.'))
    }

    const localPreview = avatarCropSrc.value
    const uploadedImage = extractProfileImage(data) || localPreview
    avatarPreview.value = uploadedImage
    form.profile_image = uploadedImage
    const imagePatch = {
      profile_pic: uploadedImage,
      image: uploadedImage,
      user_img: uploadedImage
    }
    session.updateProfileLocally(imagePatch)
    session.setUser({ ...(session.user || {}), ...imagePatch })
    closeAvatarCrop(true)

    await Swal.fire({
      icon: 'success',
      title: 'Image updated',
      text: 'Your profile image has been updated.',
      confirmButtonColor: '#2563eb'
    })
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Image upload failed',
      text: error?.message || 'Profile image could not be uploaded.',
      confirmButtonColor: '#2563eb'
    })
  } finally {
    avatarUploading.value = false
  }
}

const openAboutModal = () => {
  aboutDraft.value = form.about
  aboutModalOpen.value = true
}

const closeAboutModal = () => {
  aboutModalOpen.value = false
}

const submitDescription = async () => {
  const id = getCurrentUserId()
  if (!id || descriptionSaving.value) {
    if (!id) {
      await Swal.fire({
        icon: 'error',
        title: 'Login required',
        text: 'Sign in again before updating your description.',
        confirmButtonColor: '#2563eb'
      })
    }
    return
  }

  descriptionSaving.value = true
  try {
    const description = aboutDraft.value.trim()
    const { data } = await api.editProfile({
      type: 'description',
      description,
      id
    })

    if (!isSuccessResponse(data)) {
      throw new Error(getApiMessage(data, 'Description could not be updated.'))
    }

    form.about = description
    session.updateProfileLocally({
      about: description,
      about_me: description,
      bio: description,
      description
    })
    closeAboutModal()

    await Swal.fire({
      icon: 'success',
      title: 'Description updated',
      text: 'Your profile description has been updated.',
      confirmButtonColor: '#2563eb'
    })
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Update failed',
      text: error?.message || 'Description could not be updated.',
      confirmButtonColor: '#2563eb'
    })
  } finally {
    descriptionSaving.value = false
  }
}

const submitProfile = async () => {
  syncPhoneData()
  isSubmitting.value = true

  try {
    const id = getCurrentUserId()
    if (!id) throw new Error('Sign in again before updating your profile.')
    const contactPayload = {
      type: 'contact',
      id,
      name: form.fullName.trim(),
      phone_code: form.phone_code.trim(),
      phone: form.phone.trim(),
      email: form.email.trim()
    }
    const { data } = await api.editProfile(contactPayload)

    if (!isSuccessResponse(data)) {
      throw new Error(getApiMessage(data, 'Profile could not be updated.'))
    }

    const updatedProfile = {
      ...(session.profile || {}),
      username: form.username.trim(),
      user_name: form.username.trim(),
      name: form.fullName.trim(),
      full_name: form.fullName.trim(),
      phone_code: form.phone_code.trim(),
      phone: form.phone.trim(),
      phone_number: form.phone.trim(),
      email: form.email.trim(),
      about: form.about.trim(),
      about_me: form.about.trim(),
      bio: form.about.trim(),
      profile_pic: form.profile_image || avatarPreview.value,
      image: form.profile_image || avatarPreview.value,
      user_img: form.profile_image || avatarPreview.value
    }

    const updatedUser = {
      ...(session.user || {}),
      username: form.username.trim(),
      user_name: form.username.trim(),
      name: form.fullName.trim(),
      email: form.email.trim(),
      phone_code: form.phone_code.trim(),
      phone: form.phone.trim(),
      profile_pic: form.profile_image || avatarPreview.value,
      image: form.profile_image || avatarPreview.value,
      user_img: form.profile_image || avatarPreview.value
    }

    session.updateProfileLocally(updatedProfile)
    session.setUser(updatedUser)

    await Swal.fire({
      icon: 'success',
      title: 'Profile updated',
      text: 'Your profile details have been updated.',
      confirmButtonColor: '#2563eb'
    })

    router.push('/account')
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Update failed',
      text: error?.message || 'Profile could not be updated.',
      confirmButtonColor: '#2563eb'
    })
  } finally {
    isSubmitting.value = false
  }
}

onMounted(async () => {
  if (session.userId) {
    try {
      await session.fetchProfile()
    } catch (error) {
      console.error('Failed to preload profile:', error)
    }
  }

  hydrateForm()
  await setupPhoneInput()
})

onBeforeUnmount(() => {
  destroyPhoneInput()
})
</script>

<style scoped>
:deep(.iti) {
  width: 100%;
}

:deep(.iti__tel-input) {
  width: 100%;
}

:deep(.iti--separate-dial-code .iti__selected-country) {
  border-radius: 1rem 0 0 1rem;
}

:deep(.iti__search-input) {
  outline: none;
}
</style>
